import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import mixpanel from 'mixpanel-browser';
import { NavigateFunction, useNavigate } from 'react-router';

import useSetUserAttributes from 'hooks/useSetUserAttributes';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import Coachmark from './components/Coachmark';
import CompletionCard from './components/CompletionCard';
import TourOverlay from './components/TourOverlay';
import WelcomeCard from './components/WelcomeCard';
import { TourContextValue, TourState, TourStep } from './types';

export interface TourConfig {
  name: string;
  steps: TourStep[];
  storageKey: string;
  image: string;
  navigateToStep: (navigate: NavigateFunction, stepIndex: number, steps: TourStep[]) => void;
  onStartTour?: (navigate: NavigateFunction) => void;
}

export function createTourContext(config: TourConfig) {
  const TourContext = createContext<TourContextValue | null>(null);

  const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
      throw new Error(`use${config.name}Tour must be used within ${config.name}TourProvider`);
    }
    return context;
  };

  const TourProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();
    const { mutate: setUserAttributes } = useSetUserAttributes();
    const stateRef = useRef<TourState>();
    const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [state, setState] = useState<TourState>(() => ({
      isActive: false,
      currentStep: 0,
      hasCompletedTour: localStorage.getItem(config.storageKey) === 'true',
    }));

    // Keep ref in sync with state for use in callbacks
    stateRef.current = state;

    useEffect(() => {
      return () => {
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }
      };
    }, []);

    const navigateToStep = useCallback(
      (stepIndex: number) => {
        config.navigateToStep(navigate, stepIndex, config.steps);
      },
      [navigate]
    );

    const startTour = useCallback(
      (source?: string, skipWelcome?: boolean) => {
        mixpanel.track(MIXPANEL_EVENTS.TOUR_STARTED, { tour_type: config.name.toLowerCase(), source });
        config.onStartTour?.(navigate);
        const initialStep = skipWelcome ? 1 : 0;
        setState((prev) => ({ ...prev, isActive: true, currentStep: initialStep }));

        if (skipWelcome) {
          navigationTimeoutRef.current = setTimeout(() => {
            navigateToStep(1);
          }, 100);
        }
      },
      [navigate, navigateToStep]
    );

    const nextStep = useCallback(() => {
      const currentState = stateRef.current;
      if (!currentState) return;

      const currentStepData = config.steps[currentState.currentStep];
      mixpanel.track(MIXPANEL_EVENTS.TOUR_STEP_COMPLETED, {
        tour_type: config.name.toLowerCase(),
        step_number: currentState.currentStep,
        step_id: currentStepData?.id,
      });

      if (currentState.currentStep >= config.steps.length - 1) {
        localStorage.setItem(config.storageKey, 'true');
        setUserAttributes({ tour_completed: true });
        mixpanel.track(MIXPANEL_EVENTS.TOUR_COMPLETED, {
          tour_type: config.name.toLowerCase(),
          total_steps: config.steps.length,
        });
        setState((prev) => ({ ...prev, isActive: false, hasCompletedTour: true }));
        return;
      }

      const nextStepIndex = currentState.currentStep + 1;
      setState((prev) => ({ ...prev, currentStep: nextStepIndex }));

      // Small delay for state updates before navigating to the next step
      navigationTimeoutRef.current = setTimeout(() => {
        navigateToStep(nextStepIndex);
      }, 100);
    }, [navigateToStep, setUserAttributes]);

    const prevStep = useCallback(() => {
      const currentState = stateRef.current;
      if (!currentState) return;

      const prevStepIndex = Math.max(0, currentState.currentStep - 1);
      setState((prev) => ({ ...prev, currentStep: prevStepIndex }));

      navigationTimeoutRef.current = setTimeout(() => {
        navigateToStep(prevStepIndex);
      }, 100);
    }, [navigateToStep]);

    const endTour = useCallback(() => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      localStorage.setItem(config.storageKey, 'true');
      setUserAttributes({ tour_completed: true });
      mixpanel.track(MIXPANEL_EVENTS.TOUR_COMPLETED, {
        tour_type: config.name.toLowerCase(),
        total_steps: config.steps.length,
      });
      setState((prev) => ({ ...prev, isActive: false, hasCompletedTour: true }));
    }, [setUserAttributes]);

    const skipTour = useCallback(() => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      const currentState = stateRef.current;
      mixpanel.track(MIXPANEL_EVENTS.TOUR_SKIPPED, {
        tour_type: config.name.toLowerCase(),
        skipped_at_step: currentState?.currentStep,
      });
      localStorage.setItem(config.storageKey, 'true');
      setUserAttributes({ tour_completed: true });
      setState((prev) => ({ ...prev, isActive: false, hasCompletedTour: true }));
    }, [setUserAttributes]);

    const value = useMemo(
      () => ({
        state,
        startTour,
        nextStep,
        prevStep,
        endTour,
        skipTour,
      }),
      [state, startTour, nextStep, prevStep, endTour, skipTour]
    );

    return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
  };

  const Tour = () => {
    const { state, nextStep, prevStep, skipTour, endTour } = useTour();

    const currentStep = config.steps[state.currentStep];
    const targetId = currentStep?.type === 'coachmark' ? currentStep.targetId : undefined;
    const isCompletionCard = state.isActive && currentStep?.id === 'completion';
    const showOverlay = state.isActive && (currentStep?.type === 'coachmark' || isCompletionCard);

    // Keyboard navigation
    useEffect(() => {
      if (!state.isActive) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
          case 'ArrowRight':
            e.preventDefault();
            nextStep();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            prevStep();
            break;
          case 'Escape':
            e.preventDefault();
            skipTour();
            break;
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [state.isActive, nextStep, prevStep, skipTour]);

    return (
      <>
        <TourOverlay
          isActive={showOverlay}
          targetId={targetId}
          onClickOutside={isCompletionCard ? endTour : skipTour}
          ariaLabel={isCompletionCard ? 'Click to finish tour' : 'Click to skip tour'}
        />
        <WelcomeCard steps={config.steps} useTourHook={useTour} image={config.image} />
        {config.steps.map(
          (step, index) =>
            step.type === 'coachmark' && (
              <Coachmark key={step.id} stepIndex={index} steps={config.steps} useTourHook={useTour} />
            )
        )}
        <CompletionCard steps={config.steps} useTourHook={useTour} />
      </>
    );
  };

  return { TourContext, useTour, TourProvider, Tour };
}
